import { useState, useEffect, useCallback, useRef } from "react";
import App, { clearWpConnection } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoginScreen } from "./components/LoginScreen";
import { getSession, logout as apiLogout, type AuthUser } from "./lib/authClient";
import { hydrateKeysFromServer, clearKeys } from "./lib/apiKeys";
import { loadActiveAudit, switchSite, flushNow, clearAuditLocal } from "./lib/siteData";
import { pauseCrawlJob } from "./lib/crawlJobs";

// Top-level gate. Owns auth + per-site hydration so that, by the time <App/> mounts, the
// active site's report is already in localStorage (App reads several keys synchronously at
// first render). An explicit site switch changes `mountSiteKey` — App's `key` — so App
// remounts against the freshly-swapped localStorage.
export default function AppRoot() {
  // undefined = checking session, null = logged out, AuthUser = logged in.
  const [authUser, setAuthUser] = useState<AuthUser | null | undefined>(undefined);
  const [authNotice, setAuthNotice] = useState<string | null>(null); // e.g. "account not active" reason on boot
  const [mountSiteKey, setMountSiteKey] = useState("none"); // App remount key
  const [sitesRefreshToken, setSitesRefreshToken] = useState(0); // nudges the dropdown to re-fetch
  const isLight = (() => { try { return localStorage.getItem("seo_theme") !== "dark"; } catch { return true; } })();
  const switching = useRef(false);
  // Password-reset link support: an emailed `/?reset=<token>` link opens the "set new password"
  // screen regardless of auth state. Read the token once on mount.
  const [resetToken, setResetToken] = useState<string | null>(() => {
    try { return new URLSearchParams(window.location.search).get("reset"); } catch { return null; }
  });
  const clearResetToken = useCallback(() => {
    setResetToken(null);
    try { window.history.replaceState({}, "", window.location.pathname); } catch { /* ignore */ }
  }, []);

  // On boot: resume an existing session (hydrate keys + active site), else show login.
  useEffect(() => {
    let alive = true;
    (async () => {
      const { user, reason } = await getSession();
      if (!alive) return;
      if (user) {
        await hydrateKeysFromServer();
        const { activeSiteKey } = await loadActiveAudit();
        if (!alive) return;
        setMountSiteKey(activeSiteKey || "none");
      } else if (reason) {
        setAuthNotice(reason); // suspended mid-session → explain on the login screen
      }
      if (alive) setAuthUser(user);
    })();
    return () => { alive = false; };
  }, []);

  const handleLoggedIn = useCallback(async (user: AuthUser) => {
    // A fresh login must start clean: the WP connection (sessionStorage creds) and any report in
    // localStorage are per-browser, not per-account. Clearing them BEFORE loadActiveAudit stops a
    // new account (e.g. a team seat with no saved sites) from auto-connecting to the previous
    // account's site or adopting its leftover local report. loadActiveAudit then loads only THIS
    // account's server-side sites.
    clearWpConnection();
    clearAuditLocal();
    clearKeys();
    await hydrateKeysFromServer();
    const { activeSiteKey } = await loadActiveAudit();
    setMountSiteKey(activeSiteKey || "none");
    setAuthUser(user);
  }, []);

  const handleLogout = useCallback(async () => {
    // Pause an in-flight full audit so it doesn't keep running headless — signing back in
    // will show "Resume Audit". Read the id before clearAuditLocal wipes localStorage.
    try {
      const jobId = localStorage.getItem("lsg_active_audit_job");
      if (jobId) await pauseCrawlJob(jobId);
    } catch { /* ignore */ }
    try { await flushNow(); } catch { /* ignore */ }
    try { await apiLogout(); } catch { /* ignore */ }
    clearKeys();
    clearAuditLocal(); // don't leave one user's report in the browser for the next
    clearWpConnection(); // don't leave one user's WP connection for the next account on this tab
    setMountSiteKey("none");
    setAuthUser(null);
  }, []);

  // Switch which site's report is active (driven by the Site Storage dropdown).
  const handleSwitchSite = useCallback(async (key: string, opts?: { siteUrl?: string; label?: string }) => {
    if (switching.current || !key || key === mountSiteKey) return;
    switching.current = true;
    try {
      await switchSite(key, opts);
      setMountSiteKey(key);               // App's key changes → remount against swapped localStorage
      setSitesRefreshToken(v => v + 1);
    } finally {
      switching.current = false;
    }
  }, [mountSiteKey]);

  // App committed a new active site in place (e.g. an ad-hoc audit adopting a new domain).
  // localStorage + server are already updated by App; just refresh the dropdown — no remount.
  const handleSitesChanged = useCallback(() => {
    setSitesRefreshToken(v => v + 1);
  }, []);

  // A reset link opens the "set new password" screen even over a stale session / while checking.
  if (resetToken) {
    return <LoginScreen isLight={isLight} onLoggedIn={handleLoggedIn} resetToken={resetToken} onResetDone={clearResetToken} />;
  }
  if (authUser === undefined) {
    return <div style={{ position: "fixed", inset: 0, background: isLight ? "#f1f5f9" : "#0b0e14" }} />;
  }
  if (authUser === null) {
    return <LoginScreen isLight={isLight} onLoggedIn={handleLoggedIn} initialNotice={authNotice ?? undefined} />;
  }
  return (
    <ErrorBoundary key={mountSiteKey}>
      <App
        key={mountSiteKey}
        user={authUser}
        onLogout={handleLogout}
        sitesRefreshToken={sitesRefreshToken}
        onSwitchSite={handleSwitchSite}
        onSitesChanged={handleSitesChanged}
        onUserPatch={patch => setAuthUser(prev => (prev ? { ...prev, ...patch } : prev))}
      />
    </ErrorBoundary>
  );
}
